import { useState, useEffect, useCallback } from 'react';
// import { AuthService } from '../../services/auth/AuthService';
import {
  type UserProfile,
  type AuthTokens,
  type LoginRequest,
  type RegisterRequest,
  type AdminRegisterRequest,
  type MerchantRegisterRequest,
  type UpdateProfileRequest,
  type ChangePasswordRequest,
  type OTPVerificationRequest,
} from '../../types/api/auth';
import type { ApiErrorResponse } from '../../types/api/common';
import { useMutation } from './useApi';
import { getAuthService } from '../../service/ServiceRegistry';

/**
 * Authentication state interface
 */
interface AuthState {
  user: UserProfile | null;
  tokens: AuthTokens | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: ApiErrorResponse | null;
}

/**
 * Authentication hook for managing user authentication state
 * 
 * Features:
 * - User login/logout
 * - Registration
 * - Profile management
 * - Token management
 * - Auto-refresh tokens
 * - Cross-tab synchronization
 */
export function useAuth() {
  const [authState, setAuthState] = useState<AuthState>({
    user: null,
    tokens: null,
    isAuthenticated: false,
    isLoading: true,
    error: null,
  });

  const authService = getAuthService();

  /**
   * Initialize auth state from stored data
   */
  const initializeAuth = useCallback(() => {
    try {
      const user = authService.getCurrentUser();
      const tokens = authService.getAuthTokens();
      const isAuthenticated = authService.isAuthenticated();

      setAuthState({
        user,
        tokens,
        isAuthenticated,
        isLoading: false,
        error: null,
      });

      // Set up auto refresh if authenticated
      if (isAuthenticated) {
        authService.setupAutoRefresh();
      }
    } catch (error) {
      console.error('Failed to initialize auth:', error);
      setAuthState({
        user: null,
        tokens: null,
        isAuthenticated: false,
        isLoading: false,
        error: error as ApiErrorResponse,
      });
    }
  }, [authService]);


  /**
   * Update profile mutation
   */
  const updateProfileMutation = useMutation(
    (profileData: UpdateProfileRequest) => authService.updateProfile(profileData),
    {
      onSuccess: (response) => {
        setAuthState(prev => ({
          ...prev,
          user: response,
          error: null,
        }));
      },
      onError: (error) => {
        setAuthState(prev => ({
          ...prev,
          error,
        }));
      },
    }
  );

  /**
   * Change password mutation
   */
  const changePasswordMutation = useMutation(
    (passwordData: ChangePasswordRequest) => authService.changePassword(passwordData),
    {
      onSuccess: () => {
        setAuthState(prev => ({
          ...prev,
          error: null,
        }));
      },
      onError: (error) => {
        setAuthState(prev => ({
          ...prev,
          error,
        }));
      },
    }
  );

  /**
   * Login function (regular users)
   */
  const login = useCallback(async (credentials: LoginRequest): Promise<void> => {
    setAuthState(prev => ({ ...prev, isLoading: true, error: null }));
    
    try {
      const response = await authService.login(credentials);
      
      // Update auth state on success
      setAuthState({
        user: response.data.user,
        tokens: response.data.tokens,
        isAuthenticated: true,
        isLoading: false,
        error: null,
      });
    } catch (error) {
      // Update auth state on error
      setAuthState(prev => ({
        ...prev,
        isLoading: false,
        error: error as ApiErrorResponse,
      }));
      
      // Re-throw the error so LoginPage can catch it
      throw error;
    }
  }, [authService]);

  /**
   * Login admin function
   */
  const loginAdmin = useCallback(async (credentials: LoginRequest): Promise<void> => {
    setAuthState(prev => ({ ...prev, isLoading: true, error: null }));
    
    try {
      const response = await authService.loginAdmin(credentials);
      
      // Update auth state on success
      setAuthState({
        user: response.admin,
        tokens: response.tokens,
        isAuthenticated: true,
        isLoading: false,
        error: null,
      });
    } catch (error) {
      // Update auth state on error
      setAuthState(prev => ({
        ...prev,
        isLoading: false,
        error: error as ApiErrorResponse,
      }));
      
      // Re-throw the error so LoginPage can catch it
      throw error;
    }
  }, [authService]);

  /**
   * Login merchant function
   */
  const loginMerchant = useCallback(async (credentials: LoginRequest): Promise<void> => {
    setAuthState(prev => ({ ...prev, isLoading: true, error: null }));
    
    try {
      const response = await authService.loginMerchant(credentials);
      
      // Update auth state on success
      setAuthState({
        user: response.merchant,
        tokens: response.tokens,
        isAuthenticated: true,
        isLoading: false,
        error: null,
      });
    } catch (error) {
      // Update auth state on error
      setAuthState(prev => ({
        ...prev,
        isLoading: false,
        error: error as ApiErrorResponse,
      }));
      
      // Re-throw the error so LoginPage can catch it
      throw error;
    }
  }, [authService]);

  /**
   * Register function
   */
  const register = useCallback(async (userData: RegisterRequest): Promise<any> => {
    setAuthState(prev => ({ ...prev, isLoading: true, error: null }));
    
    try {
      const response = await authService.register(userData);
      
      // Don't update auth state for registration - user needs to verify OTP first
      setAuthState(prev => ({
        ...prev,
        isLoading: false,
        error: null,
      }));
      
      // Return response for OTP verification
      return response;
    } catch (error) {
      // Update auth state on error
      setAuthState(prev => ({
        ...prev,
        isLoading: false,
        error: error as ApiErrorResponse,
      }));
      
      // Re-throw the error so components can catch it
      throw error;
    }
  }, [authService]);

  /**
   * Register admin function
   */
  const registerAdmin = useCallback(async (userData: AdminRegisterRequest): Promise<any> => {
    setAuthState(prev => ({ ...prev, isLoading: true, error: null }));
    
    try {
      const response = await authService.registerAdmin(userData);
      
      // Don't update auth state for registration - user needs to verify OTP first
      setAuthState(prev => ({
        ...prev,
        isLoading: false,
        error: null,
      }));
      
      // Return response for OTP verification
      return response;
    } catch (error) {
      // Update auth state on error
      setAuthState(prev => ({
        ...prev,
        isLoading: false,
        error: error as ApiErrorResponse,
      }));
      
      // Re-throw the error so components can catch it
      throw error;
    }
  }, [authService]);

  /**
   * Register merchant function
   */
  const registerMerchant = useCallback(async (userData: MerchantRegisterRequest): Promise<any> => {
    setAuthState(prev => ({ ...prev, isLoading: true, error: null }));
    
    try {
      const response = await authService.registerMerchant(userData);
      
      // Don't update auth state for registration - user needs to verify OTP first
      setAuthState(prev => ({
        ...prev,
        isLoading: false,
        error: null,
      }));
      
      // Return response for OTP verification
      return response;
    } catch (error) {
      // Update auth state on error
      setAuthState(prev => ({
        ...prev,
        isLoading: false,
        error: error as ApiErrorResponse,
      }));
      
      // Re-throw the error so components can catch it
      throw error;
    }
  }, [authService]);

  /**
   * Logout function
   */
  const logout = useCallback(async (): Promise<void> => {
    setAuthState(prev => ({ ...prev, isLoading: true }));
    
    try {
      await authService.logout();
      setAuthState({
        user: null,
        tokens: null,
        isAuthenticated: false,
        isLoading: false,
        error: null,
      });
    } catch (error) {
      console.error('Logout error:', error);
      // Even if logout fails, clear local state
      setAuthState({
        user: null,
        tokens: null,
        isAuthenticated: false,
        isLoading: false,
        error: error as ApiErrorResponse,
      });
    }
  }, [authService]);

  /**
   * Logout admin function
   */
  const logoutAdmin = useCallback(async (): Promise<void> => {
    setAuthState(prev => ({ ...prev, isLoading: true }));
    
    try {
      await authService.logoutAdmin();
      setAuthState({
        user: null,
        tokens: null,
        isAuthenticated: false,
        isLoading: false,
        error: null,
      });
    } catch (error) {
      console.error('Admin logout error:', error);
      // Even if logout fails, clear local state
      setAuthState({
        user: null,
        tokens: null,
        isAuthenticated: false,
        isLoading: false,
        error: error as ApiErrorResponse,
      });
    }
  }, [authService]);

  /**
   * Logout merchant function
   */
  const logoutMerchant = useCallback(async (): Promise<void> => {
    setAuthState(prev => ({ ...prev, isLoading: true }));
    
    try {
      await authService.logoutMerchant();
      setAuthState({
        user: null,
        tokens: null,
        isAuthenticated: false,
        isLoading: false,
        error: null,
      });
    } catch (error) {
      console.error('Merchant logout error:', error);
      // Even if logout fails, clear local state
      setAuthState({
        user: null,
        tokens: null,
        isAuthenticated: false,
        isLoading: false,
        error: error as ApiErrorResponse,
      });
    }
  }, [authService]);

  /**
   * Refresh tokens function
   */
  const refreshTokens = useCallback(async (): Promise<void> => {
    try {
      const newTokens = await authService.refreshTokens();
      setAuthState(prev => ({
        ...prev,
        tokens: newTokens,
        error: null,
      }));
    } catch (error) {
      console.error('Token refresh failed:', error);
      // If refresh fails, logout user
      await logout();
    }
  }, [authService, logout]);

  /**
   * Update profile function
   */
  const updateProfile = useCallback(async (profileData: UpdateProfileRequest): Promise<void> => {
    return updateProfileMutation.mutate(profileData);
  }, [updateProfileMutation]);

  /**
   * Change password function
   */
  const changePassword = useCallback(async (passwordData: ChangePasswordRequest): Promise<void> => {
    return changePasswordMutation.mutate(passwordData);
  }, [changePasswordMutation]);

  /**
   * Verify OTP function
   */
  const verifyOTP = useCallback(async (verificationData: OTPVerificationRequest): Promise<void> => {
    setAuthState(prev => ({ ...prev, isLoading: true, error: null }));
    
    try {
      const response = await authService.verifyOTP(verificationData);
      
      // Update auth state on success
      setAuthState({
        user: response.data.user,
        tokens: response.data.tokens,
        isAuthenticated: true,
        isLoading: false,
        error: null,
      });
    } catch (error) {
      // Update auth state on error
      setAuthState(prev => ({
        ...prev,
        isLoading: false,
        error: error as ApiErrorResponse,
      }));
      
      // Re-throw the error so components can catch it
      throw error;
    }
  }, [authService]);

  /**
   * Verify customer email by user ID
   */
  const verifyCustomerEmail = useCallback(async (userId: string): Promise<void> => {
    setAuthState(prev => ({ ...prev, isLoading: true, error: null }));
    
    try {
      await authService.verifyCustomerEmail(userId);
      
      setAuthState(prev => ({
        ...prev,
        isLoading: false,
        error: null,
      }));
    } catch (error) {
      setAuthState(prev => ({
        ...prev,
        isLoading: false,
        error: error as ApiErrorResponse,
      }));
      throw error;
    }
  }, [authService]);

  /**
   * Verify admin email by user ID
   */
  const verifyAdminEmail = useCallback(async (userId: string): Promise<void> => {
    setAuthState(prev => ({ ...prev, isLoading: true, error: null }));
    
    try {
      await authService.verifyAdminEmail(userId);
      
      setAuthState(prev => ({
        ...prev,
        isLoading: false,
        error: null,
      }));
    } catch (error) {
      setAuthState(prev => ({
        ...prev,
        isLoading: false,
        error: error as ApiErrorResponse,
      }));
      throw error;
    }
  }, [authService]);

  /**
   * Verify merchant email by user ID
   */
  const verifyMerchantEmail = useCallback(async (userId: string): Promise<void> => {
    setAuthState(prev => ({ ...prev, isLoading: true, error: null }));
    
    try {
      await authService.verifyMerchantEmail(userId);
      
      setAuthState(prev => ({
        ...prev,
        isLoading: false,
        error: null,
      }));
    } catch (error) {
      setAuthState(prev => ({
        ...prev,
        isLoading: false,
        error: error as ApiErrorResponse,
      }));
      throw error;
    }
  }, [authService]);

  /**
   * Verify email by user ID and user type (unified method)
   */
  const verifyEmailByUserId = useCallback(async (userId: string, userType: string): Promise<void> => {
    setAuthState(prev => ({ ...prev, isLoading: true, error: null }));
    
    try {
      await authService.verifyEmailByUserId(userId, userType);
      
      setAuthState(prev => ({
        ...prev,
        isLoading: false,
        error: null,
      }));
    } catch (error) {
      setAuthState(prev => ({
        ...prev,
        isLoading: false,
        error: error as ApiErrorResponse,
      }));
      throw error;
    }
  }, [authService]);

  /**
   * Verify OTP by user ID and user type (unified method for OTP verification)
   */
  const verifyOTPByUserId = useCallback(async (userId: string, userType: string, otp: string): Promise<void> => {
    setAuthState(prev => ({ ...prev, isLoading: true, error: null }));
    
    try {
      await authService.verifyOTPByUserId(userId, userType, otp);
      
      setAuthState(prev => ({
        ...prev,
        isLoading: false,
        error: null,
      }));
    } catch (error) {
      setAuthState(prev => ({
        ...prev,
        isLoading: false,
        error: error as ApiErrorResponse,
      }));
      throw error;
    }
  }, [authService]);

  /**
   * Resend verification code by user type
   */
  const resendVerificationByUserId = useCallback(async (userId: string, userType: string): Promise<void> => {
    setAuthState(prev => ({ ...prev, isLoading: true, error: null }));

    try {
      await authService.resendVerificationByUserId(userId, userType);
      setAuthState(prev => ({ ...prev, isLoading: false, error: null }));
    } catch (error) {
      setAuthState(prev => ({ ...prev, isLoading: false, error: error as ApiErrorResponse }));
      throw error;
    }
  }, [authService]);

  /**
   * Check if user has specific role
   */
  const hasRole = useCallback((role: string): boolean => {
    return authService.hasRole(role);
  }, [authService]);

  /**
   * Check if user has any of the specified roles
   */
  const hasAnyRole = useCallback((roles: string[]): boolean => {
    return authService.hasAnyRole(roles);
  }, [authService]);

  /**
   * Clear auth error
   */
  const clearError = useCallback((): void => {
    setAuthState(prev => ({ ...prev, error: null }));
  }, []);

  /**
   * Set up event listeners for auth events
   */
  useEffect(() => {
    const handleAuthEvent = (event: CustomEvent) => {
      const { type } = event;
      
      switch (type) {
        case 'auth:login':
        case 'auth:register':
          initializeAuth();
          break;
        case 'auth:logout':
          setAuthState({
            user: null,
            tokens: null,
            isAuthenticated: false,
            isLoading: false,
            error: null,
          });
          break;
        case 'auth:profileUpdate':
          setAuthState(prev => ({
            ...prev,
            user: event.detail.user,
          }));
          break;
      }
    };

    // Listen for auth events
    window.addEventListener('auth:login', handleAuthEvent as EventListener);
    window.addEventListener('auth:register', handleAuthEvent as EventListener);
    window.addEventListener('auth:logout', handleAuthEvent as EventListener);
    window.addEventListener('auth:profileUpdate', handleAuthEvent as EventListener);

    // Listen for token refresh events
    const handleTokenRefresh = () => {
      refreshTokens();
    };

    window.addEventListener('token:refresh_needed', handleTokenRefresh);

    // Listen for automatic token refresh from interceptor
    const handleTokenRefreshed = () => {
      console.info('Token refreshed automatically by interceptor');
      initializeAuth(); // Re-sync state with stored tokens
    };

    window.addEventListener('auth:token_refreshed', handleTokenRefreshed);

    // Listen for cross-tab token updates
    const handleCrossTabUpdate = (event: CustomEvent) => {
      const { action } = event.detail;
      
      if (action === 'updated') {
        initializeAuth();
      } else if (action === 'cleared') {
        setAuthState({
          user: null,
          tokens: null,
          isAuthenticated: false,
          isLoading: false,
          error: null,
        });
      }
    };

    window.addEventListener('token:cross_tab_update', handleCrossTabUpdate as EventListener);

    // Cleanup
    return () => {
      window.removeEventListener('auth:login', handleAuthEvent as EventListener);
      window.removeEventListener('auth:register', handleAuthEvent as EventListener);
      window.removeEventListener('auth:logout', handleAuthEvent as EventListener);
      window.removeEventListener('auth:profileUpdate', handleAuthEvent as EventListener);
      window.removeEventListener('token:refresh_needed', handleTokenRefresh);
      window.removeEventListener('auth:token_refreshed', handleTokenRefreshed);
      window.removeEventListener('token:cross_tab_update', handleCrossTabUpdate as EventListener);
    };
  }, [initializeAuth, refreshTokens]);

  /**
   * Initialize auth on mount
   */
  useEffect(() => {
    initializeAuth();
  }, [initializeAuth]);

  return {
    // State
    user: authState.user,
    tokens: authState.tokens,
    isAuthenticated: authState.isAuthenticated,
    isLoading: authState.isLoading || updateProfileMutation.state.loading || changePasswordMutation.state.loading,
    error: authState.error || updateProfileMutation.state.error || changePasswordMutation.state.error,

    // Actions
    login,
    loginAdmin,
    loginMerchant,
    register,
    registerAdmin,
    registerMerchant,
    logout,
    logoutAdmin,
    logoutMerchant,
    refreshTokens,
    updateProfile,
    changePassword,
    verifyOTP,
    verifyCustomerEmail,
    verifyAdminEmail,
    verifyMerchantEmail,
    verifyEmailByUserId,
    verifyOTPByUserId,
    resendVerificationByUserId,
    hasRole,
    hasAnyRole,
    clearError,

    // Mutation states for specific operations
    isLoggingIn: authState.isLoading,
    isRegistering: authState.isLoading,
    isUpdatingProfile: updateProfileMutation.state.loading,
    isChangingPassword: changePasswordMutation.state.loading,
  };
}
